import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import AuthenticatedLayout from '../components/layout/AuthenticatedLayout';
import Input from '../components/ui/Input';
import Button from '../components/ui/Button';
import { authService } from '../services/authService';
import './Auth.css';

const PaymentDetails = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState('credit');
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    organization: '',
    billingAddress1: '',
    billingAddress2: '',
    city: '',
    state: '',
    zipcode: '',
    country: '',
    cardNumber: '',
    expiryMonth: '',
    expiryYear: '',
    cvv: '',
    promoCode: ''
  });
  const [errors, setErrors] = useState({});

  const handleChange = (e) => {
    const { name, value } = e.target;
    let processedValue = value;
    
    // Format card number with spaces
    if (name === 'cardNumber') {
      processedValue = value.replace(/\s/g, '').replace(/(.{4})/g, '$1 ').trim();
      if (processedValue.length > 19) processedValue = processedValue.slice(0, 19);
    }
    
    // Format expiry month (MM)
    if (name === 'expiryMonth') {
      processedValue = value.replace(/\D/g, '');
      if (processedValue.length > 2) processedValue = processedValue.slice(0, 2);
      if (processedValue.length === 2 && parseInt(processedValue) > 12) {
        processedValue = '12';
      }
    }
    
    // Format expiry year (YY)
    if (name === 'expiryYear') {
      processedValue = value.replace(/\D/g, '');
      if (processedValue.length > 2) processedValue = processedValue.slice(0, 2);
    }
    
    // Format CVV
    if (name === 'cvv') {
      processedValue = value.replace(/\D/g, '');
      if (processedValue.length > 4) processedValue = processedValue.slice(0, 4);
    }
    
    setFormData(prev => ({ ...prev, [name]: processedValue }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.firstName.trim()) newErrors.firstName = 'First name is required';
    if (!formData.lastName.trim()) newErrors.lastName = 'Last name is required';
    if (!formData.billingAddress1.trim()) newErrors.billingAddress1 = 'Billing address is required';
    if (!formData.city.trim()) newErrors.city = 'City is required';
    if (!formData.state.trim()) newErrors.state = 'State is required';
    if (!formData.zipcode.trim()) newErrors.zipcode = 'Zipcode is required';
    if (!formData.country.trim()) newErrors.country = 'Please select a country';
    if (!formData.cardNumber.trim()) newErrors.cardNumber = 'Card number is required';
    if (!formData.expiryMonth.trim()) newErrors.expiryMonth = 'Expiry month is required';
    if (!formData.expiryYear.trim()) newErrors.expiryYear = 'Expiry year is required';
    if (!formData.cvv.trim()) newErrors.cvv = 'CVV is required';
    return newErrors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const newErrors = validateForm();
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setLoading(true);
    try {
      const response = await authService.submitPaymentDetails({
        ...formData,
        paymentMethod: {
          cardNumber: formData.cardNumber,
          expiryMonth: formData.expiryMonth,
          expiryYear: formData.expiryYear,
          cvv: formData.cvv
        }
      });

      if (response.status) {
        alert('Payment successful!');
        navigate('/onboarding/organization');
      }
    } catch (err) {
      console.error('Payment error:', err);
      setErrors({ general: err.response?.data?.message || 'Payment failed. Please try again.' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthenticatedLayout>
      <div className="auth-form payment-details-container">
        <button className="back-button" onClick={() => navigate(-1)}>
          ← Back
        </button>

        <div className="payment-details-layout">
          {/* Left Side - Form */}
          <div className="payment-form-section">
            <h1 className="auth-title">Fill in your details</h1>

            {errors.general && (
              <div className="error-message general-error">
                <span className="message-text">{errors.general}</span>
                <button onClick={() => setErrors({})} className="message-close-btn">×</button>
              </div>
            )}

            <form onSubmit={handleSubmit} className="auth-form-fields">
              <div className="form-row">
                <div className="form-group form-group-half">
                  <label className="form-label">First Name</label>
                  <Input
                    name="firstName"
                    placeholder="David"
                    value={formData.firstName}
                    onChange={handleChange}
                    error={errors.firstName}
                  />
                </div>
                <div className="form-group form-group-half">
                  <label className="form-label">Last Name</label>
                  <Input
                    name="lastName"
                    placeholder="Miller"
                    value={formData.lastName}
                    onChange={handleChange}
                    error={errors.lastName}
                  />
                </div>
              </div>

              <div className="form-group">
                <label className="form-label">Organization</label>
                <Input
                  name="organization"
                  value={formData.organization}
                  onChange={handleChange}
                />
              </div>

              <div className="form-row">
                <div className="form-group form-group-half">
                  <label className="form-label">Billing Address 1</label>
                  <Input
                    name="billingAddress1"
                    value={formData.billingAddress1}
                    onChange={handleChange}
                    error={errors.billingAddress1}
                  />
                </div>
                <div className="form-group form-group-half">
                  <label className="form-label">Billing Address 2</label>
                  <Input
                    name="billingAddress2"
                    value={formData.billingAddress2}
                    onChange={handleChange}
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group form-group-third">
                  <label className="form-label">City</label>
                  <Input
                    name="city"
                    value={formData.city}
                    onChange={handleChange}
                    error={errors.city}
                  />
                </div>
                <div className="form-group form-group-third">
                  <label className="form-label">State</label>
                  <Input
                    name="state"
                    value={formData.state}
                    onChange={handleChange}
                    error={errors.state}
                  />
                </div>
                <div className="form-group form-group-third">
                  <label className="form-label">Zipcode</label>
                  <Input
                    name="zipcode"
                    value={formData.zipcode}
                    onChange={handleChange}
                    error={errors.zipcode}
                  />
                </div>
              </div>

              <div className="form-group">
                <label className="form-label">Country</label>
                <select
                  name="country"
                  value={formData.country}
                  onChange={handleChange}
                  className={`form-select ${errors.country ? 'error' : ''}`}
                >
                  <option value="">Please select a country</option>
                  <option value="United States">United States</option>
                  <option value="Canada">Canada</option>
                  <option value="United Kingdom">United Kingdom</option>
                  <option value="Australia">Australia</option>
                  <option value="Germany">Germany</option>
                  <option value="France">France</option>
                  <option value="Spain">Spain</option>
                  <option value="Italy">Italy</option>
                  <option value="Netherlands">Netherlands</option>
                  <option value="Sweden">Sweden</option>
                  <option value="Norway">Norway</option>
                  <option value="Denmark">Denmark</option>
                  <option value="Finland">Finland</option>
                  <option value="Switzerland">Switzerland</option>
                  <option value="Austria">Austria</option>
                  <option value="Belgium">Belgium</option>
                  <option value="Ireland">Ireland</option>
                  <option value="New Zealand">New Zealand</option>
                  <option value="Japan">Japan</option>
                  <option value="South Korea">South Korea</option>
                  <option value="Singapore">Singapore</option>
                  <option value="Hong Kong">Hong Kong</option>
                  <option value="Other">Other</option>
                </select>
                {errors.country && <span className="error-text">{errors.country}</span>}
              </div>

              <h2 className="section-title">Complete Your Payment</h2>

              <div className="payment-method-tabs">
                <button
                  type="button"
                  className={`tab ${paymentMethod === 'credit' ? 'active' : ''}`}
                  onClick={() => setPaymentMethod('credit')}
                >
                  Credit/Debit Card
                </button>
                <button
                  type="button"
                  className={`tab ${paymentMethod === 'bank' ? 'active' : ''}`}
                  onClick={() => setPaymentMethod('bank')}
                >
                  Bank Transfer
                </button>
              </div>

              {paymentMethod === 'credit' && (
                <>
                  <div className="form-group">
                    <label className="form-label">Card Number</label>
                    <Input
                      name="cardNumber"
                      placeholder="1234 5678 9012 3456"
                      value={formData.cardNumber}
                      onChange={handleChange}
                      error={errors.cardNumber}
                      maxLength="19"
                    />
                    <div className="card-icons">
                      <span className="card-icon visa">💳</span>
                      <span className="card-icon mastercard">💳</span>
                      <span className="card-icon amex">💳</span>
                    </div>
                  </div>

                  <div className="form-row">
                    <div className="form-group form-group-third">
                      <label className="form-label">MM</label>
                      <Input
                        name="expiryMonth"
                        placeholder="12"
                        value={formData.expiryMonth}
                        onChange={handleChange}
                        error={errors.expiryMonth}
                      />
                    </div>
                    <div className="form-group form-group-third">
                      <label className="form-label">YY</label>
                      <Input
                        name="expiryYear"
                        placeholder="25"
                        value={formData.expiryYear}
                        onChange={handleChange}
                        error={errors.expiryYear}
                      />
                    </div>
                    <div className="form-group form-group-third">
                      <label className="form-label">CVV</label>
                      <Input
                        name="cvv"
                        placeholder="123"
                        value={formData.cvv}
                        onChange={handleChange}
                        error={errors.cvv}
                      />
                    </div>
                  </div>
                </>
              )}
            </form>
          </div>

          {/* Right Side - Order Summary */}
          <div className="order-summary-section">
            <div className="order-summary-card">
              <h3 className="summary-title">Order Summary</h3>
              <div className="plan-details">
                <div className="price-display">$1,000</div>
                <p className="price-period">Per Month / Per Clinic</p>
                <p className="plan-description">
                  Professional plan for clinics that need advanced features and dedicated support.
                </p>
                <div className="plan-features">
                  <div className="feature-item">
                    <span className="feature-icon">✓</span>
                    <span>Unlimited patients</span>
                  </div>
                  <div className="feature-item">
                    <span className="feature-icon">✓</span>
                    <span>Advanced analytics</span>
                  </div>
                  <div className="feature-item">
                    <span className="feature-icon">✓</span>
                    <span>Priority support</span>
                  </div>
                  <div className="feature-item">
                    <span className="feature-icon">✓</span>
                    <span>Custom integrations</span>
                  </div>
                </div>
              </div>

              <div className="promo-code-section">
                <label className="form-label">Promo Code</label>
                <div className="promo-input-group">
                  <Input
                    name="promoCode"
                    value={formData.promoCode}
                    onChange={handleChange}
                    placeholder="Enter code"
                  />
                  <button type="button" className="apply-btn">Apply</button>
                </div>
              </div>

              <div className="summary-breakdown">
                <div className="summary-line">
                  <span>Subtotal</span>
                  <span>$1,000.00</span>
                </div>
                <div className="summary-line">
                  <span>Tax</span>
                  <span>Calculated at checkout</span>
                </div>
                <div className="summary-line total">
                  <span>Total Due Today</span>
                  <span>$1,000.00</span>
                </div>
              </div>

              <Button
                type="submit"
                variant="primary"
                size="large"
                loading={loading}
                className="submit-button"
                onClick={handleSubmit}
              >
                Place Order →
              </Button>

              <div className="security-badges">
                <div className="security-item">
                  <span className="security-icon">🔒</span>
                  <span>SSL Encrypted</span>
                </div>
                <div className="security-item">
                  <span className="security-icon">🛡️</span>
                  <span>PCI Compliant</span>
                </div>
              </div>

              <p className="terms-text">
                By clicking "Place Order", you agree to PELEEScribe's{' '}
                <a href="#" target="_blank" rel="noopener noreferrer">Terms & Conditions</a> and{' '}
                <a href="#" target="_blank" rel="noopener noreferrer">Privacy Policy</a>
              </p>
            </div>
          </div>
        </div>
      </div>
    </AuthenticatedLayout>
  );
};

export default PaymentDetails;
